import { OnInit, Directive } from '@angular/core';
import { FabricService } from '../../services/fabric.service';
import { Workspace } from '../../models/Workspace';
import { FabricItem } from '../../models/FabricItem';

export interface ParameterField {
  label: string;
  type?: string;
  value: any;
}

@Directive()
export abstract class FabricItemBaseComponent implements OnInit {
  selectedWorkspace: string = '';
  selectedWorkspaceId: string = '';
  selectedItem: FabricItem | null = null;
  workspaces: string[] = [];
  workspaceObjects: Workspace[] = [];
  isLoading: boolean = true;
  areParamsLoading: boolean = false;
  parameterFields: ParameterField[] = [];
  items: FabricItem[] = [];
  selected: FabricItem | null = null;
  isEditMode = false;
  isRunMode = false;
  runningItemIds: Set<string> = new Set();

  protected abstract get itemType(): string;
  protected abstract get jobType(): string;

  constructor(protected readonly fabricService: FabricService) { }

  ngOnInit(): void {
    this.loadWorkspaces();
  }

  loadWorkspaces(): void {
    this.isLoading = true;
    this.fabricService.getAllWorkspaces().subscribe({
      next: (workspaces: Workspace[]) => {
        if (workspaces && workspaces.length > 0) {
          this.workspaceObjects = workspaces;
          this.workspaces = workspaces.map(w => w.workspaceName);
          if (this.workspaces.length > 0) {
            this.selectedWorkspace = this.workspaces[0];
            this.selectedWorkspaceId = workspaces[0].workspaceId;
            this.loadItems();
          } else {
            this.isLoading = false;
          }
        } else {
          console.error('No workspaces returned from API');
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error fetching workspaces:', error);
        this.isLoading = false;
      }
    });
  }

  loadItems(): void {
    if (!this.selectedWorkspaceId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.fabricService.getAllItemsByType(this.selectedWorkspaceId, this.itemType).subscribe({
      next: (items: FabricItem[]) => {
        this.items = items.map(item => ({
          ...item,
          status: 'NotStarted' // Default status
        }));
        this.isLoading = false;
      },
      error: (error) => {
        console.error(`Error fetching ${this.itemType}s:`, error);
        this.items = [];
        this.isLoading = false;
      }
    });
  }

  onWorkspaceChange(): void {
    const workspace = this.workspaceObjects.find(w => w.workspaceName === this.selectedWorkspace);
    if (workspace) {
      this.selectedWorkspaceId = workspace.workspaceId;
      this.loadItems();
    }
  }

  get filteredItems() {
    return this.items;
  }

  selectItem(item: FabricItem, afterSelect?: () => void) {
    this.selectedItem = item;
    this.selected = item;
    this.isEditMode = false;
    this.areParamsLoading = true;
    this.fabricService.getItemParameters(item.workspaceId, item.id, item.type).subscribe({
      next: (parameters: any) => {
        item.parameters = parameters;
        this.areParamsLoading = false;
        this.parseParameters(item.parameters);
        if (afterSelect) afterSelect();
      },
      error: (error) => {
        console.error('Error fetching parameters: ', error);
        this.areParamsLoading = false;
      }
    });
  }

  parseParameters(parametersJson: any) {
    this.parameterFields = [];

    if (!parametersJson) {
      return;
    }

    try {
      let jsonStr = typeof parametersJson === 'string'
        ? parametersJson
        : JSON.stringify(parametersJson);

      const cleanedJson = jsonStr.replace(/\\r\\n/g, '')
        .replace(/\\/g, '')
        .replace(/"{/g, '{')
        .replace(/}"/g, '}');

      const params = JSON.parse(cleanedJson);

      this.extractParameterFields(params);
    } catch (error) {
      console.error('Error parsing parameters:', error);
      this.parameterFields = [];
    }
  }

  // Can be overridden by child classes for different parameter structures
  protected abstract extractParameterFields(params: any): void;

  createParametersJson(): string {
    if (this.parameterFields.length === 0) {
      return '{}';
    }

    const parametersObj: Record<string, any> = {};
    this.parameterFields.forEach(field => {
      parametersObj[field.label] = field.value;
    });

    return JSON.stringify(parametersObj);
  }

  runItem(itemId: string) {
    if (!itemId) {
      console.error(`No ${this.itemType} selected`);
      return;
    }

    const index = this.items.findIndex(p => p.id === itemId);
    if (index === -1) {
      console.error(`${this.itemType} with id ${itemId} not found`);
      return;
    }

    const itemToRun: FabricItem = { ...this.items[index] };

    itemToRun.parameters = this.createParametersJson();

    this.items[index].status = 'InProgress';

    if (this.selectedItem && this.selectedItem.id === itemId) {
      this.selectedItem.status = 'InProgress';
    }

    this.runningItemIds.add(itemId);

    this.fabricService.runJob(itemToRun, this.jobType).subscribe({
      next: (result: string) => {
        const newStatus = result;

        this.items[index].status = newStatus;

        if (this.selectedItem && this.selectedItem.id === itemId) {
          this.selectedItem.status = newStatus;
        }
        this.runningItemIds.delete(itemId);
      },
      error: (error) => {
        console.error(`Error running ${this.itemType}:`, error);

        this.items[index].status = 'Failed';

        if (this.selectedItem && this.selectedItem.id === itemId) {
          this.selectedItem.status = 'Failed';
        }
        this.runningItemIds.delete(itemId);
      }
    });
  }

  saveItem() {
    if (!this.selectedItem) return;

    this.selectedItem.parameters = this.createParametersJson();
    this.isEditMode = false;
  }

  editItem() {
    this.isEditMode = true;
  }
}